/**
 * Keeps the marketing caution policy consistent across asset views without
 * coupling its lineage and release-window checks to presentation code.
 */
import { DateTime } from 'luxon';

const SMART_CROP_METHOD = 'smart-crop';
const TITLE_LOGO_METHOD = 'title-logo';
const CAUTION_WINDOW_MONTHS = 3;

function isReleaseDateInWindow (
  releaseDate: string | undefined,
  windowStartMillis: number,
  windowEndMillis: number,
): boolean {
  if (releaseDate === undefined) return false;

  try {
    const parsedDate = DateTime.fromISO(releaseDate, { zone: 'utc' });
    if (!parsedDate.isValid) return false;

    const releaseDateMillis = parsedDate.toMillis();
    return releaseDateMillis >= windowStartMillis &&
      releaseDateMillis <= windowEndMillis;
  } catch {
    return false;
  }
}

export function deriveMarketingCaution (
  generationMethods: readonly string[] | undefined,
  releaseDates: ReadonlyArray<string | undefined> | undefined,
): boolean {
  if (
    !generationMethods?.includes(SMART_CROP_METHOD) ||
    !generationMethods.includes(TITLE_LOGO_METHOD) ||
    releaseDates === undefined
  ) {
    return false;
  }

  const now = DateTime.now().toUTC();
  const windowStartMillis = now
    .minus({ months: CAUTION_WINDOW_MONTHS })
    .toMillis();
  const windowEndMillis = now
    .plus({ months: CAUTION_WINDOW_MONTHS })
    .toMillis();

  return releaseDates.some((releaseDate) =>
    isReleaseDateInWindow(
      releaseDate,
      windowStartMillis,
      windowEndMillis,
    ),
  );
}
